<template>
  <div class="team-edit-overlay">
    <!-- HEADER -->
    <div class="tm-header">
      <div class="tm-header-left">
         <i class="fa fa-arrow-left tm-back" @click="cancelEdit"></i>
         <div class="tm-icon-box"><i class="fa fa-gamepad"></i></div>
         <div class="tm-header-text">
           <div class="tm-title">START GAME</div>
           <div class="tm-subtitle">CONFIGURE GAME MODE</div>
         </div>
      </div>
    </div>

    <!-- BODY -->
    <div class="tm-body">
      <div class="tm-section-header">
        <span class="tm-bar tm-bar-pink"></span> EDIT TEAMS
        <div class="tm-teams-count">{{ workingTeams.length }} TEAMS</div>
      </div>

      <div class="tm-edit-grid">
         <div v-for="team in workingTeams" :key="team.id" class="tm-edit-team-card" :style="{ borderColor: team.color }">
            <div class="tm-edit-team-header">
              <div class="tm-team-title" :style="{ color: team.color }">{{ team.name }} <i class="fa fa-pencil" style="font-size:18px; margin-left:8px; cursor: pointer;"></i></div>
              <div class="tm-players-count">{{ team.members.length }} PLAYERS</div>
            </div>
            
            <div class="tm-members-list">
              <div v-for="member in team.members" :key="member.id" class="tm-member-row">
                <div class="tm-member-info">
                   <div class="tm-avatar-img" :style="{ backgroundImage: `url(${member.avatar})`}" v-if="member.avatar"></div>
                   <div class="tm-avatar" :style="{ backgroundColor: getContrastColor(member.colorIndex) }" v-else>{{ getInitials(member.name) }}</div>
                   <span class="tm-member-name">{{ member.name }}</span>
                </div>
                <div class="tm-member-actions">
                  <button class="tm-action-btn tm-btn-switch" @click="openSwitchModal(team.id, member.id)"><i class="fa fa-exchange"></i> SWITCH</button>
                  <button class="tm-action-btn tm-btn-move" @click="openMoveModal(team.id, member.id)"><i class="fa fa-sign-out"></i> MOVE TO</button>
                </div>
              </div>
            </div>
         </div>
      </div>
    </div>

    <!-- FIXED FOOTER -->
    <div class="tm-footer-actions tm-footer-edit">
      <button class="tm-btn-cancel" @click="cancelEdit"><i class="fa fa-exchange" style="margin-right: 6px;"></i> BACK TO ORIGINAL TEAMS</button>
      <button class="tm-btn-primary" @click="saveEdit">SAVE CHANGES</button>
    </div>

    <!-- SWITCH MODAL -->
    <div v-if="showSwitchModal" class="tm-submodal-overlay" @click.self="showSwitchModal = false">
      <div class="tm-submodal tm-switch-modal">
         <div class="tm-submodal-header">
            <div class="tm-submodal-title"><i class="fa fa-exchange" style="color:#00ffff; margin-right:8px;"></i> SWITCH WITH</div>
            <div class="tm-submodal-subtitle">SELECT A STUDENT TO SWAP POSITIONS</div>
         </div>
         
         <div class="tm-submodal-body">
            <div v-for="team in switchableTeams" :key="team.id" class="tm-submodal-team-group">
               <div class="tm-submodal-team-name" :style="{ color: team.color }">
                 <span style="background: #0d2729; padding: 0 10px;">{{ team.name }}</span>
               </div>
               <div v-for="member in team.members" :key="member.id" 
                    class="tm-switch-member-row" 
                    :class="{ selected: selectedTarget && selectedTarget.playerId === member.id }"
                    @click="selectedTarget = { teamId: team.id, playerId: member.id }">
                  <div class="tm-avatar-img" :style="{ backgroundImage: `url(${member.avatar})`}" v-if="member.avatar"></div>
                  <div class="tm-avatar" :style="{ backgroundColor: getContrastColor(member.colorIndex) }" v-else>{{ getInitials(member.name) }}</div>
                  <span class="tm-member-name">{{ member.name }}</span>
               </div>
            </div>
         </div>
         
         <div class="tm-submodal-footer">
            <button class="tm-btn-primary tm-btn-full" @click="confirmSwitch" :disabled="!selectedTarget">CONFIRM</button>
         </div>
      </div>
    </div>

    <!-- MOVE TO MODAL -->
    <div v-if="showMoveModal" class="tm-submodal-overlay" @click.self="showMoveModal = false">
      <div class="tm-submodal tm-move-modal">
         <div class="tm-submodal-header">
            <div class="tm-submodal-title"><i class="fa fa-sign-out" style="color:#ff00ff; margin-right:8px;"></i> MOVE TO</div>
            <div class="tm-submodal-subtitle">SELECT DESTINATION GROUP</div>
         </div>
         
         <div class="tm-submodal-body">
            <div v-for="team in movableTeams" :key="team.id" 
                 class="tm-move-team-card" 
                 :class="{ full: team.members.length >= 10, selected: selectedTarget && selectedTarget.teamId === team.id }"
                 :style="{ borderColor: team.color }"
                 @click="team.members.length < 10 ? selectedTarget = { teamId: team.id } : null">
               <div class="tm-move-team-left">
                  <div class="tm-team-title" :style="{ color: team.color }">{{ team.name }}</div>
                  <div class="tm-team-members-preview">
                     <template v-for="(member, idx) in team.members.slice(0, 3)">
                        <div :key="member.id + '-img'" class="tm-avatar-img" :style="{backgroundImage: `url(${member.avatar})`}" v-if="member.avatar"></div>
                        <div :key="member.id + '-txt'" class="tm-avatar" :style="{backgroundColor: getContrastColor(member.colorIndex)}" v-else>
                          {{ getInitials(member.name) }}
                        </div>
                     </template>
                     <div v-if="team.members.length > 3" class="tm-avatar tm-more" style="background-color: #0f3c44; color: #00ffff">
                       +{{ team.members.length - 3 }}
                     </div>
                  </div>
               </div>
               <div class="tm-move-team-right">
                  <div v-if="team.members.length >= 10" class="tm-roster-full">ROSTER FULL</div>
                  <div class="tm-move-count">
                    <div class="tm-move-count-numbers">{{ String(team.members.length).padStart(2, '0') }}/10</div>
                    <div class="tm-move-count-label">PLAYERS</div>
                  </div>
               </div>
            </div>
         </div>
         
         <div class="tm-submodal-footer">
            <button class="tm-btn-primary tm-btn-full tm-btn-magenta" @click="confirmMove" :disabled="!selectedTarget">CONFIRM</button>
         </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "TeamEditModal",
  props: {
    initialTeams: {
      type: Array,
      required: true
    },
    coreColors: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      showSwitchModal: false,
      showMoveModal: false,
      selectedPlayer: null,
      selectedTarget: null,
      workingTeams: []
    };
  },
  computed: {
    switchableTeams() {
      if (!this.selectedPlayer) return [];
      return this.workingTeams.filter(t => t.id !== this.selectedPlayer.teamId);
    },
    movableTeams() {
      if (!this.selectedPlayer) return [];
      return this.workingTeams.filter(t => t.id !== this.selectedPlayer.teamId);
    }
  },
  mounted() {
    // Deep clone initial teams
    this.workingTeams = JSON.parse(JSON.stringify(this.initialTeams));
  },
  methods: {
    cancelEdit() {
      this.$emit('cancel');
    },
    saveEdit() {
      this.$emit('save', JSON.parse(JSON.stringify(this.workingTeams)));
    },
    getInitials(name) {
      if (!name) return "";
      const parts = name.split(" ");
      if (parts.length > 1) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    getContrastColor(index) {
      const idx = index || 0;
      const cycle = Math.floor(idx / 30);
      let base = this.coreColors[idx % 30] || '#808080';
      if (cycle > 0) {
        const dimFactor = Math.max(0.1, 1 - (cycle * 0.1));
        base = this.dimHex(base, dimFactor);
      }
      return base;
    },
    dimHex(hex, factor) {
      hex = hex.replace('#', '');
      let r = parseInt(hex.substring(0, 2), 16);
      let g = parseInt(hex.substring(2, 4), 16);
      let b = parseInt(hex.substring(4, 6), 16);
      r = Math.floor(r * factor);
      g = Math.floor(g * factor);
      b = Math.floor(b * factor);
      return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
    },
    openSwitchModal(teamId, playerId) {
      this.selectedPlayer = { teamId, playerId };
      this.selectedTarget = null;
      this.showSwitchModal = true;
    },
    openMoveModal(teamId, playerId) {
      this.selectedPlayer = { teamId, playerId };
      this.selectedTarget = null;
      this.showMoveModal = true;
    },
    confirmSwitch() {
      if (!this.selectedPlayer || !this.selectedTarget) return;
      
      const sourceTeam = this.workingTeams.find(t => t.id === this.selectedPlayer.teamId);
      const targetTeam = this.workingTeams.find(t => t.id === this.selectedTarget.teamId);
      const sourceIdx = sourceTeam.members.findIndex(m => m.id === this.selectedPlayer.playerId);
      const targetIdx = targetTeam.members.findIndex(m => m.id === this.selectedTarget.playerId);
      
      if (sourceIdx !== -1 && targetIdx !== -1) {
        const temp = sourceTeam.members[sourceIdx];
        sourceTeam.members[sourceIdx] = targetTeam.members[targetIdx];
        targetTeam.members[targetIdx] = temp;
      }
      this.showSwitchModal = false;
    },
    confirmMove() {
      if (!this.selectedPlayer || !this.selectedTarget) return;
      
      const sourceTeam = this.workingTeams.find(t => t.id === this.selectedPlayer.teamId);
      const targetTeam = this.workingTeams.find(t => t.id === this.selectedTarget.teamId);
      const sourceIdx = sourceTeam.members.findIndex(m => m.id === this.selectedPlayer.playerId);
      
      if (sourceIdx !== -1 && targetTeam.members.length < 10) {
        const player = sourceTeam.members.splice(sourceIdx, 1)[0];
        targetTeam.members.push(player);
      }
      this.showMoveModal = false;
    }
  }
};
</script>

<style scoped>
.team-edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #091315;
  color: #fff;
  font-family: Arial, sans-serif;
  z-index: 100000;
  display: flex;
  flex-direction: column;
}

/* HEADER */
.tm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 40px;
  border-bottom: 1px solid #1a3c40;
  background: #0d2525;
}

.tm-header-left {
  display: flex;
  align-items: center;
}

.tm-back {
  cursor: pointer;
  font-size: 16px;
  color: #8c99a0;
  margin-right: 16px;
  transition: 0.2s;
}
.tm-back:hover { color: #fff; }

.tm-icon-box {
  background: transparent;
  padding: 6px 0;
  color: #00ffff;
  font-size: 24px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tm-header-text {
  display: flex;
  flex-direction: column;
}

.tm-title { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
.tm-subtitle { font-size: 10px; color: #00ffff; letter-spacing: 0.5px; }

/* BODY */
.tm-body { padding: 30px 40px; overflow-y: auto; flex: 1; }

.tm-section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 24px;
  letter-spacing: 0.5px;
}

.tm-bar {
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background-color: #00ffff;
  margin-right: 8px;
}
.tm-bar-pink { background-color: #ff00ff; }

.tm-teams-count {
  margin-left: auto;
  border: 1px solid #1a3c40;
  color: #00ffff;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  background-color: #072e30;
}

/* EDIT MODE */
.tm-edit-grid { column-count: 2; column-gap: 24px; margin-bottom: 24px; }
.tm-edit-team-card {
  break-inside: avoid; -webkit-column-break-inside: avoid; display: inline-block; width: 100%; box-sizing: border-box; margin-bottom: 24px;
  background: #0d171a; border-left: 4px solid; border-radius: 8px; overflow: hidden;
}
.tm-edit-team-header { display: flex; justify-content: space-between; align-items: center; background: #121e24; border-bottom: 1px solid #1a3c40; padding: 16px 20px; }
.tm-players-count { font-size: 10px; color: #00ffff; background: rgba(0, 255, 255, 0.1); padding: 5px 10px; border-radius: 4px; font-weight: bold; border: 1px solid rgba(0, 255, 255, 0.3); }
.tm-team-title { font-size: 20px; font-weight: bold; flex: 1; text-transform: uppercase; letter-spacing: 1px;}

.tm-members-list {
  display: flex; flex-direction: column; gap: 10px; padding: 16px 20px 20px 20px;
  height: 290px; overflow-y: auto;
}
.tm-member-row { display: flex; justify-content: space-between; align-items: center; background: #162429; padding: 12px 16px; border-radius: 8px; border: none; }
.tm-member-info { display: flex; align-items: center; gap: 12px; }
.tm-member-name { font-size: 11px; font-weight: bold; text-transform: uppercase;}

.tm-avatar {
  width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: bold; color: #fff; border: 2px solid #0d2729; 
}
.tm-avatar-img {
  width: 32px; height: 32px; border-radius: 50%; background-size: cover; background-position: center; border: 2px solid #0d2729; 
}

.tm-member-actions { display: flex; gap: 8px; }
.tm-action-btn { 
  background: transparent; border: none; border-radius: 4px; padding: 8px 14px; font-size: 10px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; letter-spacing: 0.5px;
}
.tm-btn-switch { background: rgba(0, 255, 255, 0.1); color: #00ffff; }
.tm-btn-switch:hover { background: rgba(0, 255, 255, 0.2); }
.tm-btn-move { background: rgba(255, 0, 255, 0.1); color: #ff00ff; }
.tm-btn-move:hover { background: rgba(255, 0, 255, 0.2); }

/* FOOTER ACTIONS */
.tm-footer-edit { 
  display: flex; justify-content: space-between; align-items: center; 
  background: #0b1517; padding: 10px 40px; border-top: 1px solid #1a3c40; 
}

.tm-btn-primary {
  background: #00ffff; color: #092325; border: none; padding: 12px 24px; border-radius: 6px;
  cursor: pointer; font-size: 12px; font-weight: bold; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px;
  transition: 0.2s;
}
.tm-btn-primary:hover { opacity: 0.9; transform: scale(1.02); }
.tm-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

.tm-btn-cancel {
  background: transparent; border: 1px solid #ff00ff; color: #ff00ff; padding: 12px 24px; border-radius: 6px;
  cursor: pointer; font-size: 12px; font-weight: bold; letter-spacing: 0.5px; transition: 0.2s;
}
.tm-btn-cancel:hover { background: rgba(255,0,255,0.1); }

.tm-btn-magenta { background: #ff00ff; color: #fff; font-weight: bold; border-color: transparent; }
.tm-btn-full { width: 100%; justify-content: center; }


/* SUBMODALS */
.tm-submodal-overlay {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(9, 19, 21, 0.75); z-index: 1000000; display: flex; justify-content: center; align-items: center;
}
.tm-submodal {
  width: 420px; background: #0c1518; border-radius: 12px; border: 1px solid #00ffff; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.8);
}
.tm-move-modal { border-color: #ff00ff; }

.tm-submodal-header { padding: 20px; text-align: left; }
.tm-submodal-title { font-size: 14px; font-weight: bold; margin-bottom: 6px; letter-spacing: 0.5px; display: flex; align-items: center;}
.tm-submodal-subtitle { font-size: 9px; color: #8c99a0; letter-spacing: 1px; font-weight: bold;}

.tm-submodal-body { padding: 0 20px 20px 20px; max-height: 400px; overflow-y: auto; }
.tm-submodal-footer { padding: 20px; background: #0b1517; border-top: 1px solid #1a3c40; }

/* Switch inner */
.tm-submodal-team-group { margin-bottom: 16px; position: relative;}
.tm-submodal-team-name { font-size: 10px; font-weight: bold; margin-bottom: 12px; text-align: center; position: relative; }
.tm-submodal-team-name::before { content: ''; position: absolute; left: 0; top: 50%; width: 100%; height: 1px; background: #1a3c40; z-index: 0; }
.tm-submodal-team-name span { position: relative; z-index: 1; }

.tm-switch-member-row {
  display: flex; align-items: center; gap: 12px; padding: 12px; background: #112224; border-radius: 8px; margin-bottom: 8px; cursor: pointer; border: 1px solid #1a3c40; transition: 0.2s;
}
.tm-switch-member-row:hover { background: #153133; }
.tm-switch-member-row.selected { border-color: #00ffff; background: #0d2b2d; box-shadow: inset 0 0 10px rgba(0,255,255,0.1); }

/* Move inner */
.tm-move-team-card {
  display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; background: transparent; border-radius: 8px; margin-bottom: 12px; border: 1px solid; cursor: pointer; transition: 0.2s;
}
.tm-move-team-card:hover:not(.full) { background: rgba(255,255,255,0.05); }
.tm-move-team-card.selected { background: rgba(0, 255, 255, 0.05); box-shadow: inset 0 0 10px rgba(0, 255, 255, 0.2); border-color: #00ffff !important; }
.tm-move-team-card.full { opacity: 0.6; cursor: not-allowed; }
.tm-move-team-left { display: flex; flex-direction: column; gap: 12px; }
.tm-move-team-right { text-align: right; display: flex; flex-direction: column; justify-content: center; }

.tm-move-team-card .tm-team-members-preview { display: flex; align-items: center; margin-left: 8px; }
.tm-move-team-card .tm-avatar, .tm-move-team-card .tm-avatar-img { margin-left: -8px; border: 2px solid #091315; width: 28px; height: 28px; font-size: 10px; }
.tm-move-team-card .tm-avatar:first-child, .tm-move-team-card .tm-avatar-img:first-child { margin-left: 0; }

.tm-roster-full { font-size: 9px; color: #8c99a0; margin-bottom: 4px; letter-spacing: 1px; font-weight: bold; }
.tm-move-count { display: flex; flex-direction: column; align-items: flex-end; line-height: 1.2; }
.tm-move-count-numbers { font-size: 18px; font-weight: bold; color: #fff; letter-spacing: 1px; }
.tm-move-count-label { font-size: 10px; color: #8c99a0; text-transform: uppercase; letter-spacing: 0.5px; }

/* Scrollbar */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #1a3c40; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: #33ffff; }
</style>
